import { Dict } from "@mp/core";
import { SceneComponent, ComponentInteractionType, ComponentOutput } from "@mp/common";
import {
  Object3D,
  MeshBasicMaterial,
  SphereGeometry,
  Vector3,
  WebGLRenderTarget,
  WebGLRenderer,
} from "three";
import { Mesh, LoadingManager, LoaderUtils, REVISION } from "three";
import { SceneEvents } from "../scenes/SceneEvents";
import { Events } from "phaser";

const IS_IOS = isIOS();


interface Inputs {
  size: number,
  color: number;
  lineColor: number;
  hoverOpacity: number;
  textureURL: string;
  showOnStep: number;
  link: string;
}

type Outputs = {

} & ComponentOutput;

class PortalSelector extends SceneComponent {
  private static caughtMarker: WebGLRenderTarget;
  private cameraPose: any = null;
  private cameraDirty = true;
  private opacity = 1;
  private hoverOpacity = 1;
  private root: Object3D|null = null;
  private sphere: Mesh|null = null;
  private checkPlane: Mesh|null = null;
  private sphereWP = new Vector3();
  private markerVisible = false;
  private hovered = false;
  private caught = false;
  private cameraSub: { cancel(): void }|null = null;
  private hintActiveTime = 0;
  private static hintDuration = 3000;

  inputs: Inputs = {
    size: 1.0,
    color: 0xffff00,
    lineColor: 0x000000,
    hoverOpacity: 1,
    textureURL: "",
    showOnStep: null,
    link: "",
  };

  outputs = {} as Outputs;

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  constructor(private eventBus: Events.EventEmitter, private sdk: any) {
    super();
  }

  onInit() {
    const THREE = this.context.three;
    if (!PortalSelector.caughtMarker) {
      PortalSelector.createCaughtMarker(THREE.WebGLRenderTarget);
    }
    this.root = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;
    this.makeSphere();

    this.onCameraPoseChanged = this.onCameraPoseChanged.bind(this);
    this.onHintActivated = this.onHintActivated.bind(this);
    this.cameraSub = this.sdk.Camera.pose.subscribe(this.onCameraPoseChanged);
    // this.eventBus.addListener(SceneEvents.DisplayHint, this.onHintActivated);
  }

  onEvent(eventType: string, eventData: Dict): void {
    if (!this.caught) {
      if (eventType === ComponentInteractionType.CLICK) {
        this.sphere.getWorldPosition(this.sphereWP);
        this.notify(eventType, {
          position: this.sphereWP,
        });
        this.caught = true;
        // show check after click
        const planeMat = this.checkPlane.material as MeshBasicMaterial;
        planeMat.opacity = this.hoverOpacity;
        // if the portal was captured, we don't need the "hint" alwasys on top
        this.setAlwaysOnTop(false);

        window.top.location.href = this.inputs.link;
      }
      if (eventType === ComponentInteractionType.HOVER) {
        this.hovered = eventData.hover
        this.sphere.scale.multiplyScalar(1.2);
      }
    }
  }

  onTick() {
    // if (this.cameraDirty && this.cameraPose) {
    //   const target = this.cameraPose.position;
    //   this.root.lookAt(target.x, target.y, target.z);
    //   this.cameraDirty = false;
    // }

    const hintActive = this.hintActiveTime + PortalSelector.hintDuration > Date.now();
    const markerVisible = this.hovered || hintActive;
    if (this.markerVisible !== markerVisible) {
      const sphereMat = this.sphere.material as MeshBasicMaterial;
      sphereMat.opacity = markerVisible ? this.hoverOpacity : this.opacity;

      const planeMat = this.checkPlane.material as MeshBasicMaterial;
      planeMat.opacity = this.caught && markerVisible ? this.hoverOpacity : 0;

    }

    if (!this.hovered) {
      this.sphere.scale.set(1, 1, 1)
    }

    if (!this.caught && hintActive) {
      this.setAlwaysOnTop(true);
    }
    this.markerVisible = markerVisible;
  }

  onDestroy() {
    if (this.sphere) {
      this.sphere.geometry.dispose();
      (this.sphere.material as MeshBasicMaterial).dispose();
    }
    this.cameraSub.cancel();
    // this.eventBus.removeListener(SceneEvents.DisplayHint, this.onHintActivated);
  }

  private makeSphere() {
    const THREE = this.context.three;

    if (this.sphere) {
      this.root.remove(this.sphere);
      (this.sphere.material as MeshBasicMaterial).dispose();
      (this.sphere.geometry as SphereGeometry).dispose();
      this.sphere = null;
    }

    const sphereGeometry = new THREE.SphereGeometry(0.5 * this.inputs.size, 12, 10);

    const texture = new THREE.TextureLoader().load( this.inputs.textureURL );

    var sphereMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      map: texture,
      opacity: this.opacity,
      depthTest: false,
      depthWrite: false,
    });

    this.sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    this.root.add(this.sphere);

    const planeSize = 0.5 * this.inputs.size;
    this.checkPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(planeSize, planeSize),
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        color: 0x00ff00,
        map: PortalSelector.caughtMarker.texture,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
      }));
    this.checkPlane.position.z = this.inputs.size;
    this.sphere.renderOrder = 10;
    this.checkPlane.renderOrder = 11;

    this.root.add(this.checkPlane);


    // add glow effect to portal
    // via https://stemkoski.github.io/Three.js/Shader-Glow.html
  	// create custom material from the shader code above
  	// that is within specially labeled script tags
  	// const customMaterial = new THREE.ShaderMaterial(
  	// {
  	//     uniforms:
  	// 	{
  	// 		"c":   { value: 1.0 },
  	// 		"p":   { value: 2 },
  	// 		glowColor: { value: new THREE.Color(0xffffff) },
  	// 		viewVector: { value: { x: this.cameraPose.position.x, y: this.cameraPose.position.y, z: this.cameraPose.position.z } }
  	// 	},
  	// 	vertexShader:   document.getElementById( 'vertexShader'   ).textContent,
  	// 	fragmentShader: document.getElementById( 'fragmentShader' ).textContent,
  	// 	side: THREE.FrontSide,
  	// 	blending: THREE.AdditiveBlending,
  	// 	transparent: true,
  	// }   );
    //
    //
  	// const portalGlow = new THREE.Mesh(sphereGeometry, customMaterial);
    // portalGlow.position.set(this.sphere.position.x, this.sphere.position.y, this.sphere.position.z);
  	// portalGlow.scale.multiplyScalar(1.2);
  	// this.root.add( portalGlow );


  }

  private static createCaughtMarker(renderTargetFactory: typeof WebGLRenderTarget) {
    const renderTargetSize = 64;
    const canvas = document.createElement('canvas');
    const renderContext2D = canvas.getContext('2d');
    canvas.width = renderTargetSize;
    canvas.height = renderTargetSize;


    renderContext2D.textAlign = 'center';
    renderContext2D.textBaseline = 'middle';
    renderContext2D.fillStyle = '#fff';
    renderContext2D.font = renderTargetSize + 'px Roboto';
    renderContext2D.fillText('', 0.5 * renderTargetSize, 0.5 * renderTargetSize);

    // create three.js objects to render
    this.caughtMarker = new renderTargetFactory(renderTargetSize, renderTargetSize);
    this.caughtMarker.texture.image = renderContext2D.canvas;
    this.caughtMarker.texture.needsUpdate = true;
  }

  private onCameraPoseChanged(pose: any) {
    this.cameraPose = pose;
    this.cameraDirty = true;
  }

  private onHintActivated() {
    this.hintActiveTime = Date.now();
  }

  private setAlwaysOnTop(onTop: boolean) {
    const sphereMat = this.sphere.material as MeshBasicMaterial;
    sphereMat.depthTest = !onTop;
    sphereMat.depthWrite = !onTop;

    const planeMat = this.checkPlane.material as MeshBasicMaterial;
    planeMat.depthTest = !onTop;
    planeMat.depthWrite = !onTop;
  }
}

export const portalSelectorSphereType = 'guided.portalselectorsphere';

export const createPortalSelectorClosure = function(eventBus: Events.EventEmitter, sdk: any) {
  return function() {
    return new PortalSelector(eventBus, sdk);
  };
}

// https://stackoverflow.com/a/9039885/1314762
function isIOS() {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
  // iPad on iOS 13 detection
  || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
}
